import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as integrationApi from '../../services/project-integration-api.js';
import type { IntegrationType, IntegrationWithAccountDTO, GA4PropertyDTO, GSCSiteDTO, AdsCustomerDTO, IGBusinessAccountDTO } from '../../services/project-integration-api.js';
import { INTEGRATION_LABELS } from '../../services/project-integration-api.js';
import { listGoogleAccounts, type GoogleAccount } from '../../services/google-account-api.js';
import { listMetaAccounts, type MetaAccount } from '../../services/meta-account-api.js';
import * as projectApi from '../../services/company-project-api.js';
import type { CompanyDataProject } from '../../services/company-project-api.js';
import '../shared/cp-confirm-dialog.js';

interface ProjectIntegrations {
  project: CompanyDataProject;
  integrations: IntegrationWithAccountDTO[];
}

@customElement('cp-company-projects-settings')
export class CpCompanyProjectsSettings extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;

  @state() private _projectIntegrations: ProjectIntegrations[] = [];
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _googleAccounts: GoogleAccount[] = [];
  @state() private _metaAccounts: MetaAccount[] = [];

  // New-Project form
  @state() private _newName = '';
  @state() private _newUrl = '';
  @state() private _creatingProject = false;
  @state() private _createError = '';

  // Rename inline state
  @state() private _renamingId: number | null = null;
  @state() private _renameName = '';
  @state() private _renameUrl = '';
  @state() private _renameSaving = false;

  // Delete confirm
  @state() private _deleteTarget: CompanyDataProject | null = null;

  // Integration modal state
  @state() private _showModal = false;
  @state() private _modalProjectId: number | null = null;
  @state() private _modalIntegrationType: IntegrationType | null = null;
  @state() private _modalGoogleAccountId: string = '';
  @state() private _modalMetaAccountId: string = '';
  @state() private _modalConfigValue: string = '';
  @state() private _modalConfigOptions: { value: string; label: string }[] = [];
  @state() private _modalLoadingOptions = false;
  @state() private _modalSaving = false;
  @state() private _modalError = '';

  // Test connection states
  @state() private _testingProjectId: number | null = null;
  @state() private _testingType: IntegrationType | null = null;
  @state() private _testResults: Map<string, integrationApi.TestConnectionResult> = new Map();

  connectedCallback(): void {
    super.connectedCallback();
    this._load();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId') && this.companyId > 0) {
      void this._load();
    }
  }

  private async _load(): Promise<void> {
    if (!this.companyId) return;
    this._loading = true;
    this._error = '';
    try {
      const [projects, googleAccounts, metaAccounts] = await Promise.all([
        projectApi.listDataProjects(this.companyId),
        listGoogleAccounts(),
        listMetaAccounts(),
      ]);
      this._googleAccounts = googleAccounts;
      this._metaAccounts = metaAccounts;
      await this._loadIntegrations(projects);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private async _loadIntegrations(projects: CompanyDataProject[]): Promise<void> {
    const results = await Promise.all(
      projects.map(async (project) => {
        try {
          const integrations = await integrationApi.listIntegrations(project.id);
          return { project, integrations };
        } catch {
          return { project, integrations: [] };
        }
      }),
    );
    this._projectIntegrations = results;
  }

  // --- Project CRUD ---

  private async _createProject(): Promise<void> {
    const name = this._newName.trim();
    if (!name) { this._createError = 'Name ist erforderlich'; return; }
    this._creatingProject = true;
    this._createError = '';
    try {
      await projectApi.createDataProject(this.companyId, {
        name,
        url: this._newUrl.trim() || null,
      });
      this._newName = '';
      this._newUrl = '';
      await this._load();
    } catch (e) {
      this._createError = e instanceof Error ? e.message : 'Fehler beim Anlegen';
    } finally {
      this._creatingProject = false;
    }
  }

  private _startRename(project: CompanyDataProject): void {
    this._renamingId = project.id;
    this._renameName = project.name;
    this._renameUrl = project.url ?? '';
  }

  private _cancelRename(): void {
    this._renamingId = null;
    this._renameName = '';
    this._renameUrl = '';
  }

  private async _saveRename(projectId: number): Promise<void> {
    const name = this._renameName.trim();
    if (!name) return;
    this._renameSaving = true;
    try {
      await projectApi.updateDataProject(this.companyId, projectId, {
        name,
        url: this._renameUrl.trim() || null,
      });
      this._cancelRename();
      await this._load();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._renameSaving = false;
    }
  }

  private _askDelete(project: CompanyDataProject): void {
    this._deleteTarget = project;
  }

  private _cancelDelete = (): void => {
    this._deleteTarget = null;
  };

  private _confirmDelete = async (): Promise<void> => {
    if (!this._deleteTarget) return;
    const target = this._deleteTarget;
    this._deleteTarget = null;
    try {
      await projectApi.deleteDataProject(this.companyId, target.id);
      await this._load();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen';
    }
  };

  // --- Integration helpers ---

  private _getIntegrationLabel(type: IntegrationType) {
    return INTEGRATION_LABELS.find((l) => l.type === type);
  }

  private _getExistingTypesForProject(projectId: number): IntegrationType[] {
    const pi = this._projectIntegrations.find((p) => p.project.id === projectId);
    return pi ? pi.integrations.map((i) => i.integration_type) : [];
  }

  // --- Integration modal ---

  private _openAddModal(projectId: number, integrationType: IntegrationType): void {
    this._showModal = true;
    this._modalProjectId = projectId;
    this._modalIntegrationType = integrationType;
    this._modalGoogleAccountId = this._googleAccounts[0]?.id?.toString() ?? '';
    this._modalMetaAccountId = this._metaAccounts[0]?.id?.toString() ?? '';
    this._modalConfigValue = '';
    this._modalConfigOptions = [];
    this._modalSaving = false;
    this._modalError = '';
    this._modalLoadingOptions = false;
  }

  private _closeModal(): void {
    this._showModal = false;
    this._modalProjectId = null;
    this._modalIntegrationType = null;
  }

  private async _loadConfigOptions(): Promise<void> {
    if (this._modalConfigOptions.length > 0) return;

    const type = this._modalIntegrationType;
    const label = this._getIntegrationLabel(type!);
    if (!label) return;

    if (label.accountKind === 'google') {
      const accountId = Number(this._modalGoogleAccountId);
      if (!accountId) return;
      this._modalLoadingOptions = true;
      try {
        let options: { value: string; label: string }[] = [];
        if (type === 'ga4') {
          const items = await integrationApi.listGA4Properties(accountId);
          options = items.map((p: GA4PropertyDTO) => ({ value: p.propertyId, label: p.displayName || p.propertyId }));
        } else if (type === 'gsc') {
          const items = await integrationApi.listGSCSites(accountId);
          options = items.map((s: GSCSiteDTO) => ({ value: s.siteUrl, label: s.siteUrl }));
        } else if (type === 'ads') {
          const items = await integrationApi.listAdsCustomers(accountId);
          options = items.map((c: AdsCustomerDTO) => ({ value: c.customerId, label: c.customerId }));
        }
        this._modalConfigOptions = options;
        if (options.length > 0 && !this._modalConfigValue) {
          this._modalConfigValue = options[0].value;
        }
      } catch (e) {
        this._modalError = e instanceof Error ? e.message : 'Fehler beim Laden der Optionen';
      } finally {
        this._modalLoadingOptions = false;
      }
    } else if (label.accountKind === 'meta') {
      const accountId = Number(this._modalMetaAccountId);
      if (!accountId) return;
      this._modalLoadingOptions = true;
      try {
        const items = await integrationApi.listIGBusinessAccounts(accountId);
        this._modalConfigOptions = items.map((a: IGBusinessAccountDTO) => ({ value: a.igUserId, label: `@${a.username}` }));
        if (items.length > 0 && !this._modalConfigValue) {
          this._modalConfigValue = items[0].igUserId;
        }
      } catch (e) {
        this._modalError = e instanceof Error ? e.message : 'Fehler beim Laden der Optionen';
      } finally {
        this._modalLoadingOptions = false;
      }
    }
  }

  private async _saveIntegration(): Promise<void> {
    if (!this._modalProjectId || !this._modalIntegrationType) return;
    const type = this._modalIntegrationType;
    const label = this._getIntegrationLabel(type);
    if (!label) return;

    if (!this._modalConfigValue) {
      this._modalError = 'Bitte waehle einen Wert aus';
      return;
    }

    this._modalSaving = true;
    this._modalError = '';
    try {
      const configJson: Record<string, unknown> = {};
      if (type === 'ga4') configJson.property_id = this._modalConfigValue;
      else if (type === 'gsc') configJson.site_url = this._modalConfigValue;
      else if (type === 'ads') configJson.customer_id = this._modalConfigValue;
      else if (type === 'ig') configJson.ig_user_id = this._modalConfigValue;

      const data: { google_account_id?: number; meta_account_id?: number; config_json: Record<string, unknown> } = { config_json: configJson };
      if (label.accountKind === 'google') data.google_account_id = Number(this._modalGoogleAccountId);
      else data.meta_account_id = Number(this._modalMetaAccountId);

      await integrationApi.upsertIntegration(this._modalProjectId, type, data);
      this._closeModal();
      await this._load();
    } catch (e) {
      this._modalError = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._modalSaving = false;
    }
  }

  private async _deleteIntegration(projectId: number, type: IntegrationType): Promise<void> {
    try {
      await integrationApi.deleteIntegration(projectId, type);
      await this._load();
    } catch {
      this._error = 'Fehler beim Entfernen der Integration';
    }
  }

  private async _testIntegration(projectId: number, type: IntegrationType): Promise<void> {
    this._testingProjectId = projectId;
    this._testingType = type;
    const key = `${projectId}-${type}`;
    try {
      const result = await integrationApi.testConnection(projectId, type);
      this._testResults = new Map(this._testResults).set(key, result);
    } catch (e) {
      this._testResults = new Map(this._testResults).set(key, {
        ok: false,
        message: e instanceof Error ? e.message : 'Verbindungstest fehlgeschlagen',
      });
    } finally {
      this._testingProjectId = null;
      this._testingType = null;
    }
  }

  // --- Rendering ---

  private _renderIntegrationRow(projectId: number, integration: IntegrationWithAccountDTO) {
    const label = this._getIntegrationLabel(integration.integration_type);
    const key = `${projectId}-${integration.integration_type}`;
    const testResult = this._testResults.get(key);
    const isTesting = this._testingProjectId === projectId && this._testingType === integration.integration_type;

    const accountName = integration.google_account_alias ?? integration.meta_account_alias ?? '–';
    const configJson = integration.config_json ?? {};
    const configValue = (configJson.property_id ?? configJson.site_url ?? configJson.customer_id ?? configJson.ig_user_id ?? null) as string | null;

    return html`
      <div class="cp-projects-settings__integration">
        <span class="cp-projects-settings__integration-icon">${label?.icon ?? ''}</span>
        <div class="cp-projects-settings__integration-info">
          <div class="cp-projects-settings__integration-type">${label?.label ?? integration.integration_type}</div>
          <div class="cp-projects-settings__integration-account">Account: ${accountName}</div>
          ${configValue ? html`
            <div class="cp-projects-settings__integration-config">${configValue}</div>
          ` : nothing}
        </div>
        ${testResult ? html`
          <span class="cp-projects-settings__integration-status ${testResult.ok ? 'cp-projects-settings__integration-status--ok' : testResult.needsReauth ? 'cp-projects-settings__integration-status--reauth' : 'cp-projects-settings__integration-status--error'}">
            ${testResult.ok ? 'Verbunden' : testResult.needsReauth ? 'Neu autorisieren' : testResult.message ?? 'Fehler'}
          </span>
        ` : nothing}
        <div class="cp-projects-settings__integration-actions">
          <button
            class="cp-projects-settings__btn cp-projects-settings__btn--test"
            @click=${() => this._testIntegration(projectId, integration.integration_type)}
            ?disabled=${isTesting}
          >${isTesting ? '...' : 'Test'}</button>
          <button
            class="cp-projects-settings__btn cp-projects-settings__btn--remove"
            @click=${() => this._deleteIntegration(projectId, integration.integration_type)}
          >Entfernen</button>
        </div>
      </div>
    `;
  }

  private _renderAddButtons(projectId: number) {
    const existing = this._getExistingTypesForProject(projectId);
    const missing = INTEGRATION_LABELS.filter((l) => !existing.includes(l.type));
    if (missing.length === 0) return nothing;

    return html`
      ${missing.map((label) => html`
        <button
          class="cp-projects-settings__btn cp-projects-settings__btn--add"
          @click=${() => this._openAddModal(projectId, label.type)}
        >
          ${label.icon} + ${label.label}
        </button>
      `)}
    `;
  }

  private _renderModal() {
    if (!this._showModal || !this._modalIntegrationType) return nothing;

    const label = this._getIntegrationLabel(this._modalIntegrationType);
    const isGoogle = label?.accountKind === 'google';

    const configLabel = this._modalIntegrationType === 'ga4' ? 'Property'
      : this._modalIntegrationType === 'gsc' ? 'Site URL'
      : this._modalIntegrationType === 'ads' ? 'Customer ID'
      : 'IG Account';

    return html`
      <div class="cp-projects-settings__modal-backdrop" @click=${(e: Event) => { if (e.target === e.currentTarget) this._closeModal(); }}>
        <div class="cp-projects-settings__modal">
          <div class="cp-projects-settings__modal-header">
            <h3 class="cp-projects-settings__modal-title">${label?.icon ?? ''} ${label?.label ?? ''} verknuepfen</h3>
            <button class="cp-projects-settings__modal-close" @click=${this._closeModal}>&times;</button>
          </div>
          <div class="cp-projects-settings__modal-body">
            ${this._modalError ? html`
              <p class="cp-projects-settings__error">${this._modalError}</p>
            ` : nothing}

            ${isGoogle ? html`
              <div class="cp-projects-settings__form-group">
                <label class="cp-projects-settings__form-label">Google Account</label>
                <select
                  class="cp-projects-settings__form-select"
                  .value=${this._modalGoogleAccountId}
                  @change=${(e: Event) => {
                    this._modalGoogleAccountId = (e.target as HTMLSelectElement).value;
                    this._modalConfigOptions = [];
                    this._modalConfigValue = '';
                  }}
                >
                  ${this._googleAccounts.map((a) => html`
                    <option value=${a.id}>${a.account_alias} (${a.google_email})</option>
                  `)}
                </select>
                ${this._googleAccounts.length === 0 ? html`
                  <p class="cp-projects-settings__form-hint">Kein Google Account verbunden. Bitte zuerst in den Einstellungen verbinden.</p>
                ` : nothing}
              </div>
            ` : html`
              <div class="cp-projects-settings__form-group">
                <label class="cp-projects-settings__form-label">Meta Account</label>
                <select
                  class="cp-projects-settings__form-select"
                  .value=${this._modalMetaAccountId}
                  @change=${(e: Event) => {
                    this._modalMetaAccountId = (e.target as HTMLSelectElement).value;
                    this._modalConfigOptions = [];
                    this._modalConfigValue = '';
                  }}
                >
                  ${this._metaAccounts.map((a) => html`
                    <option value=${a.id}>${a.account_alias}</option>
                  `)}
                </select>
                ${this._metaAccounts.length === 0 ? html`
                  <p class="cp-projects-settings__form-hint">Kein Meta Account verbunden. Bitte zuerst in den Einstellungen verbinden.</p>
                ` : nothing}
              </div>
            `}

            <div class="cp-projects-settings__form-group">
              <div style="display:flex;align-items:center;justify-content:space-between;">
                <label class="cp-projects-settings__form-label">${configLabel}</label>
                ${this._modalConfigOptions.length === 0 && !this._modalLoadingOptions ? html`
                  <button
                    class="cp-projects-settings__btn cp-projects-settings__btn--test"
                    @click=${this._loadConfigOptions}
                    ?disabled=${(isGoogle && !this._modalGoogleAccountId) || (!isGoogle && !this._modalMetaAccountId)}
                  >Optionen laden</button>
                ` : nothing}
              </div>
              ${this._modalLoadingOptions ? html`
                <p class="cp-projects-settings__form-hint">Laden...</p>
              ` : this._modalConfigOptions.length > 0 ? html`
                <select
                  class="cp-projects-settings__form-select"
                  .value=${this._modalConfigValue}
                  @change=${(e: Event) => { this._modalConfigValue = (e.target as HTMLSelectElement).value; }}
                >
                  ${this._modalConfigOptions.map((opt) => html`
                    <option value=${opt.value} ?selected=${this._modalConfigValue === opt.value}>${opt.label}</option>
                  `)}
                </select>
              ` : html`
                <input
                  type="text"
                  class="cp-projects-settings__form-select"
                  placeholder="${configLabel} eingeben"
                  .value=${this._modalConfigValue}
                  @input=${(e: Event) => { this._modalConfigValue = (e.target as HTMLInputElement).value; }}
                />
              `}
            </div>

            <div class="cp-projects-settings__form-actions">
              <button
                class="cp-projects-settings__btn cp-projects-settings__btn--cancel"
                @click=${this._closeModal}
              >Abbrechen</button>
              <button
                class="cp-projects-settings__btn cp-projects-settings__btn--save"
                @click=${this._saveIntegration}
                ?disabled=${this._modalSaving || !this._modalConfigValue}
              >${this._modalSaving ? 'Speichert...' : 'Speichern'}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderNewProjectForm() {
    return html`
      <div class="cp-projects-settings__project">
        <div class="cp-projects-settings__project-header">
          <span class="cp-projects-settings__project-icon">＋</span>
          <div style="flex:1;display:flex;flex-direction:column;gap:var(--spacing-xs);">
            <input
              type="text"
              class="cp-projects-settings__form-select"
              placeholder="Projekt-Name (z.B. rockstardevelopers.de)"
              .value=${this._newName}
              @input=${(e: Event) => { this._newName = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this._createProject(); }}
            />
            <input
              type="text"
              class="cp-projects-settings__form-select"
              placeholder="URL (optional, z.B. https://rockstardevelopers.de)"
              .value=${this._newUrl}
              @input=${(e: Event) => { this._newUrl = (e.target as HTMLInputElement).value; }}
            />
          </div>
          <button
            class="cp-projects-settings__btn cp-projects-settings__btn--save"
            @click=${this._createProject}
            ?disabled=${this._creatingProject || !this._newName.trim()}
          >${this._creatingProject ? '...' : 'Anlegen'}</button>
        </div>
        ${this._createError ? html`<p class="cp-projects-settings__error">${this._createError}</p>` : nothing}
      </div>
    `;
  }

  private _renderProjectHeader(project: CompanyDataProject) {
    if (this._renamingId === project.id) {
      return html`
        <div class="cp-projects-settings__project-header">
          <span class="cp-projects-settings__project-icon">📁</span>
          <div style="flex:1;display:flex;flex-direction:column;gap:var(--spacing-xs);">
            <input
              type="text"
              class="cp-projects-settings__form-select"
              .value=${this._renameName}
              @input=${(e: Event) => { this._renameName = (e.target as HTMLInputElement).value; }}
            />
            <input
              type="text"
              class="cp-projects-settings__form-select"
              placeholder="URL (optional)"
              .value=${this._renameUrl}
              @input=${(e: Event) => { this._renameUrl = (e.target as HTMLInputElement).value; }}
            />
          </div>
          <button
            class="cp-projects-settings__btn cp-projects-settings__btn--save"
            @click=${() => this._saveRename(project.id)}
            ?disabled=${this._renameSaving || !this._renameName.trim()}
          >${this._renameSaving ? '...' : 'Speichern'}</button>
          <button
            class="cp-projects-settings__btn cp-projects-settings__btn--cancel"
            @click=${this._cancelRename}
          >Abbrechen</button>
        </div>
      `;
    }

    return html`
      <div class="cp-projects-settings__project-header">
        <span class="cp-projects-settings__project-icon">📁</span>
        <div style="flex:1;">
          <p class="cp-projects-settings__project-name">${project.name}</p>
          ${project.url ? html`<p class="cp-projects-settings__project-path">${project.url}</p>` : nothing}
        </div>
        <button
          class="cp-projects-settings__btn cp-projects-settings__btn--test"
          @click=${() => this._startRename(project)}
          title="Umbenennen"
        >Umbenennen</button>
        <button
          class="cp-projects-settings__btn cp-projects-settings__btn--remove"
          @click=${() => this._askDelete(project)}
          title="Loeschen"
        >Loeschen</button>
      </div>
    `;
  }

  override render() {
    if (this._loading) {
      return html`
        <div class="cp-projects-settings">
          <p class="cp-projects-settings__empty">Laden...</p>
        </div>
      `;
    }

    return html`
      <div class="cp-projects-settings">
        ${this._error ? html`<p class="cp-projects-settings__error">${this._error}</p>` : nothing}

        ${this._renderNewProjectForm()}

        ${this._projectIntegrations.length === 0 ? html`
          <p class="cp-projects-settings__empty">Noch keine Datenaggregations-Projekte angelegt.</p>
        ` : nothing}

        ${this._projectIntegrations.map((pi) => html`
          <div class="cp-projects-settings__project">
            ${this._renderProjectHeader(pi.project)}
            <div class="cp-projects-settings__project-body">
              ${pi.integrations.length > 0 ? html`
                ${pi.integrations.map((i) => this._renderIntegrationRow(pi.project.id, i))}
              ` : html`
                <p class="cp-projects-settings__empty" style="padding:var(--spacing-sm) 0">Keine Integrationen konfiguriert</p>
              `}
              ${this._renderAddButtons(pi.project.id)}
            </div>
          </div>
        `)}

        ${this._renderModal()}

        <cp-confirm-dialog
          .open=${this._deleteTarget !== null}
          title="Projekt loeschen"
          message="Projekt '${this._deleteTarget?.name ?? ''}' und alle verknuepften Integrationen endgueltig loeschen?"
          confirmLabel="Loeschen"
          variant="danger"
          @dialog-confirmed=${this._confirmDelete}
          @dialog-cancelled=${this._cancelDelete}
        ></cp-confirm-dialog>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-projects-settings': CpCompanyProjectsSettings;
  }
}
